import { useEffect, useRef, useState } from "react";
import { Button, Card, Input, message, Modal, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, DownOutlined, ExpandOutlined, PlayCircleOutlined, SaveOutlined, UpOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import FlowCanvas from "../components/flows/FlowCanvas";
import { promptApi } from "../services/promptApi";
import { googleWorkspaceApi } from "../services/googleWorkspaceApi";
import { repairMojibakeText } from "../services/apiClient";

const { Text, Title } = Typography;
const statusColors = { completed: "green", failed: "red", running: "blue", partial: "orange", pending: "gold", queued: "gold" };

export default function ScriptDetail() {
  const { id } = useParams();
  const history = useHistory();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningAll, setRunningAll] = useState(false);
  const [runningStepId, setRunningStepId] = useState(null);
  const [expandedIoStepId, setExpandedIoStepId] = useState(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [workspaceConnections, setWorkspaceConnections] = useState([]);
  const addingStepRef = useRef(false);

  useEffect(() => {
    Promise.all([promptApi.scripts.get(id), promptApi.providers.list(), promptApi.assistants.list(), googleWorkspaceApi.connection()])
      .then(([found, providerItems, assistantItems, workspaceConnection]) => { setRun(found); setProviders(providerItems); setAssistants(assistantItems); setWorkspaceConnections(workspaceConnection?.connected ? [workspaceConnection] : []); })
      .catch((error) => message.error(error.message))
      .finally(() => setLoading(false));
  }, [id]);

  const shouldPollStatus = Boolean(run && (run.status === "running" || run.steps?.some((step) => ["running", "queued"].includes(step.status))));
  useEffect(() => {
    if (!shouldPollStatus) return undefined;
    let cancelled = false;
    const pollStatus = async () => {
      try {
        const updated = await promptApi.scripts.status(id);
        if (!cancelled) setRun(updated);
      } catch (_) {
        // Keep the current screen state; the next interval can recover.
      }
    };
    const intervalId = window.setInterval(pollStatus, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [id, shouldPollStatus]);

  if (loading) return <Card><Spin /> Đang tải kịch bản...</Card>;
  if (!run) return <Card>Không tìm thấy bản ghi.</Card>;

  const expandedIoStep = run.steps.find((step) => step.id === expandedIoStepId);
  const change = (stepId, values) => setRun((current) => ({ ...current, steps: current.steps.map((step) => step.id === stepId ? { ...step, ...values } : step) }));
  const stepsById = new Map(run.steps.map((step) => [step.id, step]));
  const displaySteps = run.steps.map((step) => {
    const parent = step.parentId ? stepsById.get(step.parentId) : null;
    return { ...step, runtimePrompt: parent ? (parent.output || "") : (run.script || "") };
  });
  const runPayload = () => ({
    script: run.script,
    steps: run.steps.map((step, index) => ({ id: step.id, parentId: step.parentId, nodeType: step.nodeType || "ai", name: step.name, providerId: step.providerId, assistantId: step.assistantId, prompt: step.prompt, executionMode: step.executionMode || "sync", config: step.config || {}, order: step.order ?? index })),
  });
  const createStep = async (parentId, prompt = "") => {
    if (addingStepRef.current) return;
    const providerId = providers[0]?.id;
    const assistantId = assistants[0]?.id;
    if (!providerId || !assistantId) return message.warning("Vui lòng tạo nhà cung cấp và trợ lý trước khi thêm bước");
    addingStepRef.current = true;
    try {
      const updated = await promptApi.scripts.createStep(run.id, {
        parentId,
        nodeType: "ai",
        name: parentId ? "Bước xử lý mới" : "Phân tích đầu vào",
        providerId,
        assistantId,
        prompt,
        executionMode: "sync",
      });
      setRun(updated);
      message.success("Đã thêm bước");
    } catch (error) { message.error(error.message); }
    finally {
      addingStepRef.current = false;
    }
  };
  const addRoot = () => createStep(null);
  const addNext = (stepId) => {
    const parent = run.steps.find((step) => step.id === stepId);
    createStep(stepId, parent?.output || "");
  };
  const remove = async (stepId) => {
    try {
      const updated = await promptApi.scripts.removeStep(run.id, stepId);
      setRun(updated);
      if (expandedIoStepId === stepId || !updated.steps.some((step) => step.id === expandedIoStepId)) {
        setExpandedIoStepId(null);
      }
      message.success("Đã xóa bước");
    } catch (error) { message.error(error.message); }
  };
  const validateRun = () => {
    if (!run.script?.trim()) {
      message.warning("Vui lòng nhập kịch bản trước khi chạy");
      return false;
    }
    if (!run.steps.length) {
      message.warning("Kịch bản cần có ít nhất một bước");
      return false;
    }
    if (run.steps.some((step) => (step.nodeType || "ai") === "ai" && (!step.providerId || !step.assistantId))) {
      message.warning("Vui lòng chọn nhà cung cấp và trợ lý cho đầy đủ các node AI");
      return false;
    }
    return true;
  };
  const saveChanges = async () => {
    if (!validateRun()) return;
    setSaving(true);
    try {
      const updated = await promptApi.scripts.updateSteps(run.id, runPayload());
      setRun(updated);
      message.success("Đã lưu thay đổi kịch bản");
    } catch (error) { message.error(error.message); }
    finally { setSaving(false); }
  };
  const runAll = async () => {
    if (!validateRun()) return;
    setRunningAll(true);
    setRun((current) => ({
      ...current,
      status: "running",
      steps: current.steps.map((step) => !step.parentId ? { ...step, status: "running", errorMessage: null, duration: "" } : step),
    }));
    try {
      const updated = await promptApi.scripts.runAll(run.id, runPayload());
      setRun(updated);
      updated.status === "failed" ? message.error(updated.errorMessage || "Chạy luồng thất bại") : message.success("Đã chạy cả luồng");
    } catch (error) { message.error(error.message); }
    finally { setRunningAll(false); }
  };
  const runOneStep = async (step) => {
    if (!run.script?.trim()) return message.warning("Vui lòng nhập kịch bản trước khi chạy");
    if ((step.nodeType || "ai") === "ai" && (!step.providerId || !step.assistantId)) return message.warning("Vui lòng chọn nhà cung cấp và trợ lý cho bước này");
    setRunningStepId(step.id);
    setRun((current) => ({
      ...current,
      status: "running",
      steps: current.steps.map((item) => item.id === step.id ? { ...item, status: "running", errorMessage: null, duration: "" } : item),
    }));
    try {
      const updated = await promptApi.scripts.runStep(run.id, step.id, { providerId: step.providerId, assistantId: step.assistantId, prompt: step.prompt, script: run.script, steps: runPayload().steps });
      setRun(updated);
      const updatedStep = updated.steps.find((item) => item.id === step.id);
      updatedStep?.status === "failed" ? message.error(updatedStep.errorMessage || "Node chạy lỗi") : message.success(`Đã chạy “${repairMojibakeText(step.name)}”`);
    } catch (error) {
      setRun((current) => {
        const steps = current.steps.map((item) => item.id === step.id ? { ...item, status: "failed", errorMessage: error.message } : item);
        const hasCompleted = steps.some((item) => item.status === "completed");
        const hasFailed = steps.some((item) => item.status === "failed");
        return { ...current, status: hasCompleted && hasFailed ? "partial" : hasFailed ? "failed" : current.status, steps };
      });
      message.error(error.message);
    }
    finally { setRunningStepId(null); }
  };
  const resultPanel = (step) => <div className="flow-editor-node-run">
    <div className="flow-node-run-actions">
      <Button size="small" type="primary" icon={<PlayCircleOutlined />} loading={runningStepId === step.id} onClick={() => runOneStep(step)}>Chạy</Button>
      <Tooltip title="Phóng to kết quả"><Button type="text" size="small" className="flow-expand-button" aria-label={`Phóng to kết quả bước ${step.name}`} icon={<ExpandOutlined />} onClick={() => setExpandedIoStepId(step.id)} /></Tooltip>
    </div>
    {step.errorMessage && <div className="flow-node-io-preview flow-node-error-preview"><div className="flow-node-io-text has-error">{step.errorMessage}</div></div>}
  </div>;

  return <>
    <div className="page-heading"><Space><Tooltip title="Quay lại danh sách kịch bản"><Button className="flow-icon-button flow-back-button" aria-label="Quay lại danh sách kịch bản" icon={<ArrowLeftOutlined />} onClick={() => history.push("/scripts")} /></Tooltip><div><Title level={3}>{run.title}</Title><Space><Tag color={statusColors[run.status]}>{run.status}</Tag><Text type="secondary">{new Date(run.createdAt).toLocaleString("vi-VN")} · {run.flowName}</Text></Space></div></Space><Space><Button icon={<SaveOutlined />} loading={saving} onClick={saveChanges}>Lưu thay đổi</Button><Button type="primary" icon={<PlayCircleOutlined />} loading={runningAll} onClick={runAll}>Chạy cả luồng</Button></Space></div>
    <Card className={`script-source ${sourceOpen ? "script-source-open" : ""}`}>
      <div className="script-source-header">
        <Text type="secondary">KỊCH BẢN ĐẦU VÀO</Text>
        <Tooltip title={sourceOpen ? "Đóng nội dung" : "Mở nội dung"}>
          <Button className="flow-icon-button script-source-toggle" aria-label={sourceOpen ? "Đóng nội dung kịch bản đầu vào" : "Mở nội dung kịch bản đầu vào"} icon={sourceOpen ? <UpOutlined /> : <DownOutlined />} onClick={() => setSourceOpen((value) => !value)} />
        </Tooltip>
      </div>
      {sourceOpen && <Input.TextArea value={run.script} autoSize={{ minRows: 5, maxRows: 14 }} onChange={(event) => setRun((current) => ({ ...current, script: event.target.value }))} />}
    </Card>
    <Card className="flow-editor-card"><FlowCanvas steps={displaySteps} providers={providers} assistants={assistants} workspaceConnections={workspaceConnections} editable configEditable promptEditable structuralActions promptLabel="Prompt" runtimePromptLabel="Prompt" onChange={change} onRuntimePromptChange={(value) => setRun((current) => ({ ...current, script: value }))} onDelete={remove} onAddRoot={addRoot} onAddNext={addNext} renderExtra={resultPanel} /></Card>
    <Modal wrapClassName="flow-prompt-modal-wrap" className="flow-prompt-modal flow-io-modal" title={expandedIoStep ? `Kết quả · ${expandedIoStep.name}` : "Kết quả"} visible={Boolean(expandedIoStep)} onCancel={() => setExpandedIoStepId(null)} footer={<Button onClick={() => setExpandedIoStepId(null)}>Đóng</Button>} width="calc(100vw - 32px)">
      <div className="flow-prompt-modal-meta"><div><Text type="secondary">Nhà cung cấp AI</Text><Text strong>{providers.find((item) => item.id === expandedIoStep?.providerId)?.name || expandedIoStep?.providerName || "Chưa chọn"}</Text></div><div><Text type="secondary">Trợ lý AI</Text><Text strong>{assistants.find((item) => item.id === expandedIoStep?.assistantId)?.name || expandedIoStep?.assistantName || "Chưa chọn"}</Text></div></div>
      <div className="flow-io-modal-grid"><div className="flow-io-modal-field"><Text strong>Prompt</Text><Input.TextArea value={expandedIoStep ? (displaySteps.find((step) => step.id === expandedIoStep.id)?.runtimePrompt || "") : ""} readOnly /></div><div className="flow-io-modal-field"><Text strong>Kết quả</Text><Input.TextArea readOnly value={expandedIoStep?.output || expandedIoStep?.errorMessage || ""} /></div></div>
    </Modal>
  </>;
}
